import axios from 'axios';

// Use current origin for API calls (works for both dev-scm and fact-scm)
// Falls back to localhost for local development
const API_BASE_URL = process.env.REACT_APP_API_URL ||
  (window.location.hostname === 'localhost' ? 'http://localhost:3116' : window.location.origin);

// Create axios instance
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response) {
      // Handle 401 unauthorized
      if (error.response.status === 401) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('userPermissions');
        if (window.location.pathname !== '/login') {
          window.location.href = '/login';
        }
      }

      // Requests made with responseType:'blob' (file downloads/exports) still
      // get their error body delivered as a Blob, not parsed JSON — read it
      // back to text so the real server message surfaces instead of the
      // generic fallback below.
      let data = error.response.data;
      if (data instanceof Blob && data.type && data.type.includes('json')) {
        try {
          data = JSON.parse(await data.text());
        } catch (_) { /* leave data as-is, fall back below */ }
      }

      // Handle other errors
      const errorMessage = data?.message || data?.error || 'An error occurred';
      const rejectedError = new Error(errorMessage);
      // Many call sites read err.response.data for structured fields beyond
      // the message (upgrade_required, feature_required, validation details,
      // etc.) — preserve that shape here (with the blob-corrected `data`
      // above) so those checks keep working instead of silently seeing
      // `undefined` and falling back to a generic message.
      rejectedError.response = { ...error.response, data };
      return Promise.reject(rejectedError);
    } else if (error.request) {
      return Promise.reject(new Error('Network error. Please check your connection.'));
    } else {
      return Promise.reject(error);
    }
  }
);

export default apiClient;
